import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Heart, Calendar as CalendarIcon, Bell, Trash2, Download, 
  MapPin, Clock, Ticket, Plus, Loader2 
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  
  // Alert dialog
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [alertDate, setAlertDate] = useState("");
  const [alertTime, setAlertTime] = useState("10:00");
  const [alertType, setAlertType] = useState("event"); // event or ticket
  const [creatingAlert, setCreatingAlert] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [favRes, alertsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/favorites`, { headers }),
        axios.get(`${API}/ticket-alerts`, { headers }),
        axios.get(`${API}/events?limit=100`, { headers })
      ]);
      
      setFavorites(favRes.data || []);
      setAlerts(alertsRes.data || []);
      setEvents(eventsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (eventId) => {
    try {
      await axios.delete(`${API}/favorites/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(f => f.event_id !== eventId));
      toast.success("ELIMINADO DE FAVORITOS");
    } catch (error) {
      toast.error("ERROR AL ELIMINAR");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/ticket-alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      toast.success("ALERTA ELIMINADA");
    } catch (error) {
      toast.error("ERROR AL ELIMINAR");
    }
  };

  const handleDownloadCalendar = async (alertId) => {
    try {
      window.open(`${API}/ticket-alerts/${alertId}/calendar`, '_blank');
    } catch (error) {
      toast.error("ERROR AL DESCARGAR");
    }
  };

  const openAlertDialog = (event, type = "event") => {
    setSelectedEvent(event);
    setAlertType(type);
    
    // Set default date based on type
    if (type === "event") {
      // Alert 1 day before the event
      const eventDate = new Date(event.event_date);
      eventDate.setDate(eventDate.getDate() - 1);
      setAlertDate(eventDate.toISOString().split('T')[0]);
      setAlertTime("10:00");
    } else {
      // For tickets, default to today
      setAlertDate(new Date().toISOString().split('T')[0]);
      setAlertTime("10:00");
    }
    
    setAlertDialogOpen(true);
  };

  const handleCreateAlert = async () => {
    if (!alertDate || !alertTime || !selectedEvent) {
      toast.error("COMPLETA TODOS LOS CAMPOS");
      return;
    }
    
    // Check if ticket alert requires ticket_url
    if (alertType === "ticket" && !selectedEvent.ticket_url) {
      toast.error("ESTE EVENTO NO TIENE LINK DE ENTRADAS");
      return;
    }
    
    setCreatingAlert(true);
    try {
      const alertDateTime = new Date(`${alertDate}T${alertTime}`);
      
      await axios.post(`${API}/ticket-alerts`, {
        event_id: selectedEvent.event_id,
        event_title: selectedEvent.title || selectedEvent.event_title,
        alert_datetime: alertDateTime.toISOString(),
        ticket_url: selectedEvent.ticket_url || "",
        alert_type: alertType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(alertType === "event" ? "RECORDATORIO CREADO" : "ALERTA DE ENTRADAS CREADA");
      setAlertDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR ALERTA");
    } finally {
      setCreatingAlert(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white mb-2">MI PANEL</h1>
          <p className="text-zinc-500 font-mono text-sm">
            Gestiona tus eventos favoritos y alertas
          </p>
        </div>

        <Tabs defaultValue="favorites" className="space-y-6">
          <TabsList className="bg-[#111] border border-zinc-800 rounded-none p-1">
            <TabsTrigger 
              value="favorites" 
              className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Heart className="w-4 h-4 mr-2" />
              MIS EVENTOS ({favorites.length})
            </TabsTrigger>
            <TabsTrigger 
              value="alerts" 
              className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white"
            >
              <Bell className="w-4 h-4 mr-2" />
              ALERTAS ({alerts.length})
            </TabsTrigger>
          </TabsList>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black text-white">EVENTOS GUARDADOS</h2>
              </div>
              
              {favorites.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 font-mono">NO TIENES EVENTOS GUARDADOS</p>
                  <Button
                    onClick={() => navigate("/events")}
                    className="mt-4 bg-primary text-white font-mono"
                  >
                    EXPLORAR EVENTOS
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {favorites.map((fav) => (
                    <div key={fav.event_id} className="bg-zinc-900/50 border border-zinc-800 overflow-hidden group">
                      {fav.image_url && (
                        <div 
                          className="h-32 bg-cover bg-center cursor-pointer"
                          style={{ backgroundImage: `url(${fav.image_url})` }}
                          onClick={() => navigate(`/events/${fav.event_id}`)}
                        />
                      )}
                      <div className="p-4">
                        <h3 
                          className="font-bold text-white mb-2 cursor-pointer hover:text-primary"
                          onClick={() => navigate(`/events/${fav.event_id}`)}
                        >
                          {fav.title || fav.event_title}
                        </h3>
                        <div className="text-zinc-500 font-mono text-xs space-y-1 mb-3">
                          <p className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {formatDate(fav.event_date)}
                          </p>
                          {fav.bar_name && (
                            <p className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {fav.bar_name}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAlertDialog(fav, "event")}
                            className="flex-1 border-zinc-700 text-zinc-400 font-mono text-xs"
                            data-testid={`remind-btn-${fav.event_id}`}
                          >
                            <Bell className="w-3 h-3 mr-1" />
                            RECORDAR
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAlertDialog(fav, "ticket")}
                            disabled={!fav.ticket_url}
                            title={!fav.ticket_url ? "Este evento no tiene link de entradas" : "Crear alerta de entradas"}
                            className={`flex-1 border-zinc-700 font-mono text-xs ${
                              fav.ticket_url ? "text-zinc-400" : "text-zinc-600 cursor-not-allowed"
                            }`}
                            data-testid={`ticket-btn-${fav.event_id}`}
                          >
                            <Ticket className="w-3 h-3 mr-1" />
                            ENTRADAS
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveFavorite(fav.event_id)}
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`remove-fav-${fav.event_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-black text-white">MIS ALERTAS</h2>
              </div>
              
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 font-mono">NO TIENES ALERTAS CONFIGURADAS</p>
                  <p className="text-zinc-600 font-mono text-sm mt-2">
                    Añade eventos a favoritos para crear recordatorios
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <div 
                      key={alert.alert_id} 
                      className="bg-zinc-900/50 border border-zinc-800 p-4 flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <h3 className="font-bold text-white mb-1">{alert.event_title}</h3>
                        <div className="text-zinc-500 font-mono text-xs flex flex-wrap gap-4">
                          <span className="flex items-center gap-1">
                            <Bell className="w-3 h-3" />
                            Alerta: {formatDate(alert.alert_datetime)} {formatTime(alert.alert_datetime)}
                          </span>
                          <span className={`px-2 py-0.5 ${
                            alert.alert_type === "ticket" ? "bg-yellow-500/20 text-yellow-400" : "bg-blue-500/20 text-blue-400"
                          }`}>
                            {alert.alert_type === "ticket" ? "ENTRADAS" : "EVENTO"}
                          </span>
                          {alert.notified && (
                            <span className="text-green-500">✓ NOTIFICADO</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadCalendar(alert.alert_id)}
                          className="border-zinc-700 text-zinc-400"
                          title="Descargar al calendario"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAlert(alert.alert_id)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Alert Creation Dialog */}
      <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-white">
              {alertType === "ticket" ? "ALERTA DE ENTRADAS" : "RECORDATORIO DE EVENTO"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedEvent && (
              <div className="bg-zinc-900/50 border border-zinc-700 p-3">
                <p className="text-white font-bold">{selectedEvent.title || selectedEvent.event_title}</p>
                <p className="text-zinc-500 font-mono text-xs">
                  Evento: {formatDate(selectedEvent.event_date)}
                </p>
              </div>
            )}
            
            <div>
              <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                {alertType === "ticket" ? "¿CUÁNDO QUIERES RECORDAR COMPRAR?" : "¿CUÁNDO TE AVISAMOS?"}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="date"
                  value={alertDate}
                  onChange={(e) => setAlertDate(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white font-mono"
                />
                <Input
                  type="time"
                  value={alertTime}
                  onChange={(e) => setAlertTime(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white font-mono"
                />
              </div>
            </div>
            
            <p className="text-zinc-600 font-mono text-xs">
              {alertType === "ticket" 
                ? "Recibirás un email y podrás añadirlo a tu calendario."
                : "Te recordaremos este evento en la fecha y hora seleccionadas."
              }
            </p>
            
            <Button
              onClick={handleCreateAlert}
              disabled={creatingAlert}
              className="w-full bg-primary text-white font-mono"
            >
              {creatingAlert ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              CREAR ALERTA
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
